import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import s from './ModelVariantUnits.module.scss';
import PropTypes from 'prop-types';
import { initialModelVariantState } from '../../reducers/initialState';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import Button from 'react-bootstrap/Button';
import ModernCheckbox from '../WSAControls/ModernCheckbox/ModernCheckbox';
import classNames from 'classnames';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { isRequired } from '../../utils';
import Cookies from 'js-cookie';
import ModelVariantConfigurationContentTemplate from '../ModelVariantConfiguration/ModelVariantConfigurationContentTemplate';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb';
import { getModelVariantManager } from '../../selectors/index';

const ModelVariantUnits = ({
  userProfileWidgets,
  modelVariantUnitManager = { ...initialModelVariantState.modelVariantManager.selectedModelVariant.ModelVariantUnit },
  saveModelVariantUnits,
  getModelVariantUnits,
  setModelVariantUnitsChange,
  intl
}) => {
  const [searchText, setSearchText] = useState('');
  const [localformErrors, setFormErrors] = useState({});
  const envName = process.env.REACT_APP_ENV_NAME_SHORT;
  let { ModelCategoryId, ModelVariantId, ModelId } = useParams();
  let history = useNavigate();
  let modelVariantManager = useSelector(state => getModelVariantManager(state));

  let filter = modelVariantUnitManager.filter || '';
  let offset = modelVariantUnitManager.offset || 0;
  let limit = modelVariantUnitManager.limit || 1000;

  // Selected UnitIds for quick checks
  const selectedUnitIds = useMemo(() => {
    return modelVariantUnitManager.SelectedModelVariantUnits?.map(u => u.UnitId) || [];
  }, [modelVariantUnitManager.SelectedModelVariantUnits]);

  // Prepare data for ModernCheckbox
  const checkboxData = useMemo(() => {
    const unallocatedList = modelVariantUnitManager.UnallocatedUnitsList || [];
    return unallocatedList.map(unit => ({
      SKEY: unit.UnitId,
      label: unit.Name,
      value: unit.UnitId,
      UnitId: unit.UnitId,
      UnitNumber: unit.UnitNumber || unit.Name,
      ModelVariantName: unit.ModelVariantName || 'N/A',
      InstallationCity: unit.InstallationCity || 'N/A',
      InstallationCountry: unit.InstallationCountry || 'N/A',
      InstallationStreetAddress: unit.InstallationStreetAddress || 'N/A',
      // keep for display convenience; selection state is driven by selectedKeys below
      isChecked: selectedUnitIds.includes(unit.UnitId)
    }));
  }, [modelVariantUnitManager.UnallocatedUnitsList, selectedUnitIds]);

  // Fetch
  useEffect(() => {
    const item = {
      Offset: offset,
      Limit: limit,
      Filter: filter,
      ModelVariantId: ModelVariantId,
      ModelId: ModelId
    };
    getModelVariantUnits(item);
  }, [getModelVariantUnits, ModelCategoryId, ModelId, ModelVariantId, filter, offset, limit]);

  // Clean up selection on unmount
  useEffect(() => {
    return () => {
      setModelVariantUnitsChange({ SelectedModelVariantUnits: [] });
    };
  }, [setModelVariantUnitsChange]);

  // Selection handler adapted to new ModernCheckbox payload
  const onCheckboxSelectionChange = (e, payload) => {
    let selectedUnits = [...(modelVariantUnitManager.SelectedModelVariantUnits || [])];

    if (payload.isSelectAll) {
      const rows = payload.rows || [];
      const rowIds = new Set(rows.map(r => r.UnitId));
      if (payload.targetState) {
        // add all filtered rows that aren't already selected
        rows.forEach(r => {
          const original = modelVariantUnitManager.UnallocatedUnitsList?.find(u => u.UnitId === r.UnitId);
          if (original && !selectedUnits.find(su => su.UnitId === original.UnitId)) {
            selectedUnits.push(original);
          }
        });
      } else {
        // remove all filtered rows from selection
        selectedUnits = selectedUnits.filter(u => !rowIds.has(u.UnitId));
      }
      setModelVariantUnitsChange({ SelectedModelVariantUnits: selectedUnits });
      return;
    }

    // single row toggle: { row, checked }
    const { row, checked } = payload;
    if (checked) {
      const original = modelVariantUnitManager.UnallocatedUnitsList?.find(u => u.UnitId === row.UnitId);
      if (original && !selectedUnits.find(su => su.UnitId === original.UnitId)) {
        selectedUnits.push(original);
      }
    } else {
      selectedUnits = selectedUnits.filter(u => u.UnitId !== row.UnitId);
    }
    setModelVariantUnitsChange({ SelectedModelVariantUnits: selectedUnits });
  };

  const searchHandler = (value) => setSearchText(value);
  const backUrl = `/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/model-variant-configuration/${ModelVariantId}/model-variant-units-list`;

  const cancelHandler = () => {
    history(backUrl);
    return '';
  };

  // Save
  const submitForm = e => {
    e.preventDefault();
    if (!validateInput()) return;

    const saveData = {
      ModelVariantId: parseInt(ModelVariantId),
      ModelId: parseInt(ModelId),
      Units: modelVariantUnitManager.SelectedModelVariantUnits,
      UpdatedBy: Cookies.get(`userid-${envName}`) || 0
    };
    saveModelVariantUnits(saveData);
  };

  const validateInput = () => {
    let formErrors = {};
    let isValid = true;

    if (
      !modelVariantUnitManager ||
      !modelVariantUnitManager.SelectedModelVariantUnits?.length > 0 ||
      isRequired(modelVariantUnitManager.SelectedModelVariantUnits, 1)
    ) {
      formErrors.SelectedModelVariantUnits = intl.formatMessage({
        id: 'modelVariant.units.unitsRequire',
        defaultMessage: 'Please select units'
      });
      isValid = false;
    }

    setFormErrors(formErrors);
    return isValid;
  };

  let messageId = (modelVariantUnitManager && modelVariantUnitManager.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const breadcrumbItems = [
    { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
    { label: <FormattedMessage id="model.models" defaultMessage="Models" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-list` },
    { label: <FormattedMessage id="model.modelvariant" defaultMessage="Model Variant" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/model-variants-list` },
    { label: <FormattedMessage id="modelVariant.units.manageModelVariantUnits" defaultMessage="Manage Model Variant Units" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/model-variant-configuration/${ModelVariantId}/model-variant-units-list` },
    { label: <FormattedMessage id="modelVariant.units.allocateModelVariantUnits" defaultMessage="Allocate Model Variant Units" /> }
  ];

  let allocationText = intl.formatMessage({ id: 'modelVariant.units.allocateSelectedModelVariantUnits', defaultMessage: 'Allocate All Units to the Model Variant' });
  allocationText = `${allocationText} ${modelVariantManager && modelVariantManager.selectedModelVariant && modelVariantManager.selectedModelVariant.Name || ''}`;

  return (
    <div className={s.modelVariantUnit}>
      {modelVariantUnitManager.isLoading && <LoadingSpinner />}
      {modelVariantUnitManager.showBanner && (
        <Banner
          failureText={messageText}
          showBanner={modelVariantUnitManager.showBanner}
          status={modelVariantUnitManager.isOpSuccessfull}
          successText={messageText}
        />
      )}
      {modelVariantUnitManager.isOpSuccessfull && !modelVariantUnitManager.showBanner ? <Navigate to={backUrl} /> : ''}
      <ModelVariantConfigurationContentTemplate selectedPage="manageModelVariantUnits" userProfileWidgets={userProfileWidgets} intl={intl}>
        <div className={s.contentWrapper}>
          <div className={s.componentHeader}>
            <div className={s.subHeading}>
              <FormattedMessage id="modelVariant.units.allocateModelVariantUnits" defaultMessage="Manage Model Variant Units" />
            </div>
          </div>
          <div className='pb-3'>
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className={s.modelvariantUnitAllocation}>
            {/* Modern Checkbox Component */}
            <div className={s.checkboxContainer}>
              <div className='mb-3'>
                <h3>
                  {allocationText}
                </h3>
              </div>

              <ModernCheckbox
                data={checkboxData}
                columns={['label', 'UnitNumber', 'ModelVariantName', 'InstallationCity', 'InstallationCountry']}
                columnLabels={{
                  label: intl.formatMessage({ id: 'modelVariant.units.unitName', defaultMessage: 'Unit Name' }),
                  UnitNumber: intl.formatMessage({ id: 'modelVariant.units.unitNumber', defaultMessage: 'Unit Number' }),
                  ModelVariantName: intl.formatMessage({ id: 'modelVariant.units.modelVariant', defaultMessage: 'Model Variant' }),
                  InstallationCity: intl.formatMessage({ id: 'modelVariant.units.city', defaultMessage: 'City' }),
                  InstallationCountry: intl.formatMessage({ id: 'modelVariant.units.country', defaultMessage: 'Country' })
                }}
                selectedKeys={new Set(selectedUnitIds)}
                onSelectionChange={onCheckboxSelectionChange}
                onSearchChange={searchHandler}
                searchText={searchText}
                showSelectAll={true}
                controlData={{
                  searchPlaceholder: intl.formatMessage({
                    id: 'modelVariant.units.search',
                    defaultMessage: 'Search units'
                  }),
                  emptyResults: intl.formatMessage({
                    id: 'modelVariant.units.noUnitsforsearch',
                    defaultMessage: 'No units found for the search criteria'
                  }),
                  emptyMessage: intl.formatMessage({
                    id: 'modelVariant.units.noUnitsfound',
                    defaultMessage: 'No units found'
                  }),
                  showTable: true
                }}
                disable={modelVariantUnitManager.isLoading}
              />
            </div>

            {/* Error Display */}
            {localformErrors.SelectedModelVariantUnits && (
              <div className={s.errorMessage}>
                {localformErrors.SelectedModelVariantUnits}
              </div>
            )}

            {/* Warning Message */}
            <div className={s.warningMessage}>
              <FormattedMessage
                id="model.componentpositions.unitShdowUpdatedWithLatestSettings"
                defaultMessage="Device shadow will be updated with latest model variant setttings."
              />
            </div>

            {/* Action Buttons */}
            <div className={s.actionButtons}>
              <Button
                variant="primary"
                className={classNames(s.margin5, s.btnSaveChanges)}
                onClick={submitForm}
                noValidate
                data-unittest="saveData"
              >
                <FormattedMessage id="model.componentpositions.save" defaultMessage="SAVE" />
              </Button>
              <Button
                variant="outline-secondary"
                className={classNames(s.btnCancel)}
                onClick={cancelHandler}
                noValidate
                data-unittest="saveCancel"
              >
                <FormattedMessage id="model.componentpositions.cancel" defaultMessage="CANCEL" />
              </Button>
            </div>
          </div>
        </div>
      </ModelVariantConfigurationContentTemplate>
    </div>
  );
};

ModelVariantUnits.propTypes = {
  setModelVariantUnitsChange: PropTypes.func.isRequired,
  saveModelVariantUnits: PropTypes.func.isRequired
};

export default injectIntl(ModelVariantUnits);
